/**
 * @file Targeting validation logic based on database rules
 * @module targeting/targetingValidation
 */

/**
 * Check if a target is valid based on database targeting rules
 * @param {Object} params - Targeting validation parameters
 * @param {Object} params.targetingRules - Database targeting rules for the order
 * @param {string} params.deployedZone - Zone where the order was deployed
 * @param {string} params.targetZone - Zone being targeted
 * @param {string} params.targetPlayer - Player being targeted ('friendly' or 'opponent')
 * @param {Object} params.targetUnit - The specific unit being targeted (if single unit targeting)
 * @returns {boolean} True if the target is valid according to the rules
 */
export const isValidTarget = ({ targetingRules, deployedZone, targetZone, targetPlayer, targetUnit }) => {
  if (!targetingRules) return false;

  // Check if targeting is required
  if (targetingRules.targeting === 'none') return false;

  // Check zone restrictions
  if (targetingRules.zones === 'same') {
    // Can only target the same zone where deployed
    if (targetZone !== deployedZone) return false;
  } else if (targetingRules.zones === 'both') {
    // Can target any zone
    // No zone restriction
  }

  // Check player restrictions
  if (targetingRules.player === 'friendly') {
    // Can only target friendly units
    if (targetPlayer !== 'friendly') return false;
  } else if (targetingRules.player === 'opponent') {
    // Can only target opponent units
    if (targetPlayer !== 'opponent') return false;
  } else if (targetingRules.player === 'both') {
    // Can target both friendly and opponent units
    // No player restriction
  }

  // Check targeting type
  if (targetingRules.targeting === 'single unit') {
    // Must target a specific unit
    if (!targetUnit) return false;
  } else if (targetingRules.targeting === 'zone') {
    // Must target a zone (not a specific unit)
    if (targetUnit) return false;
  }

  return true;
};

/**
 * Get valid target zones based on database rules
 * @param {Object} targetingRules - Database targeting rules
 * @param {string} deployedZone - Zone where the order was deployed
 * @returns {Array<string>} Array of valid target zones
 */
export const getValidTargetZones = (targetingRules, deployedZone) => {
  if (!targetingRules) return [];

  if (targetingRules.zones === 'same') {
    return [deployedZone];
  } else if (targetingRules.zones === 'other') {
    return deployedZone === 'front' ? ['flank'] : ['front'];
  } else if (targetingRules.zones === 'both') {
    return ['front', 'flank'];
  }

  return [];
};

/**
 * Get valid target players based on database rules
 * @param {Object} targetingRules - Database targeting rules
 * @returns {Array<string>} Array of valid target players
 */
export const getValidTargetPlayers = (targetingRules) => {
  if (!targetingRules) return [];

  if (targetingRules.player === 'friendly') {
    return ['friendly'];
  } else if (targetingRules.player === 'opponent') {
    return ['opponent'];
  } else if (targetingRules.player === 'both') {
    return ['friendly', 'opponent'];
  }

  return [];
};

/**
 * Check if a unit can be targeted based on database rules
 * @param {Object} params - Unit targeting parameters
 * @param {Object} params.targetingRules - Database targeting rules
 * @param {string} params.deployedZone - Zone where the order was deployed
 * @param {Object} params.unit - The unit being checked
 * @param {string} params.unitZone - Zone where the unit is located
 * @param {string} params.unitPlayer - Player who owns the unit
 * @returns {boolean} True if the unit can be targeted
 */
export const canTargetUnit = ({ targetingRules, deployedZone, unit, unitZone, unitPlayer }) => {
  if (!targetingRules || !unit) return false;

  // Check if targeting is required
  if (targetingRules.targeting === 'none') return false;

  // Check zone restrictions
  if (targetingRules.zones === 'same') {
    if (unitZone !== deployedZone) return false;
  } else if (targetingRules.zones === 'other') {
    if (unitZone === deployedZone) return false;
  } else if (targetingRules.zones === 'both') {
    // Can target any zone (no zone restriction)
    // This is the case for Unit 14
  }

  // Check player restrictions
  if (targetingRules.player === 'friendly' && unitPlayer !== 'friendly') return false;
  if (targetingRules.player === 'opponent' && unitPlayer !== 'opponent') return false;
  if (targetingRules.player === 'both') {
    // Can target both friendly and opponent units (no player restriction)
    // This is the case for Unit 14
  }

  // Check if general can be targeted
  if (targetingRules.general === false && unit.isGeneral) return false;

  return true;
};

/**
 * Get all valid targets for a deployed order
 * @param {Object} params - Targeting parameters
 * @param {Object} params.targetingRules - Database targeting rules
 * @param {string} params.deployedZone - Zone where the order was deployed
 * @param {Object} params.gameState - Current game state
 * @param {string} params.playerId - Current player ID
 * @returns {Array<Object>} Array of valid targets
 */
export const getValidTargets = ({ targetingRules, deployedZone, gameState, playerId }) => {
  if (!targetingRules || !gameState) return [];

  const validTargets = [];
  const opponentId = Object.keys(gameState.state).find(id => id !== playerId);
  const player = gameState.state[playerId];
  const opponent = gameState.state[opponentId];

  if (!player || !opponent) return [];

  // Get valid zones
  const validZones = getValidTargetZones(targetingRules, deployedZone);
  
  // Get valid players
  const validPlayers = getValidTargetPlayers(targetingRules);

  // Check each zone
  validZones.forEach(zone => {
    // Check player units
    if (validPlayers.includes('friendly') && player.playedOrders[zone]) {
      player.playedOrders[zone].forEach((unit, index) => {
        if (unit && canTargetUnit({ targetingRules, deployedZone, unit, unitZone: zone, unitPlayer: 'friendly' })) {
          validTargets.push({
            unit,
            zone,
            index,
            playerId,
            playerType: 'friendly',
            targetZone: zone,
            targetIndex: index
          });
        }
      });
    }

    // Check opponent units
    if (validPlayers.includes('opponent') && opponent.playedOrders[zone]) {
      opponent.playedOrders[zone].forEach((unit, index) => {
        if (unit && canTargetUnit({ targetingRules, deployedZone, unit, unitZone: zone, unitPlayer: 'opponent' })) {
          validTargets.push({
            unit,
            zone,
            index,
            playerId: opponentId,
            playerType: 'opponent',
            targetZone: zone,
            targetIndex: index
          });
        }
      });
    }
  });

  return validTargets;
};
